// Next inlines NEXT_PUBLIC_* at build time, so a missing value fails the build
// rather than production traffic. That matters for the guarded ones because
// they all fail silently: an absent platform domain makes every host look like
// a custom domain, and an absent API URL leaves axios throwing ERR_INVALID_URL
// per request.
//
// The platform key is deliberately absent from this file — it lives in
// env-server.ts, because the browser reaches the API through the proxy now and
// a NEXT_PUBLIC_ credential is readable by every visitor.
export const required = (name: string, value: string | undefined): string => {
  if (!value) throw new Error(`Missing required environment variable: ${name}`);
  return value;
};

export const API_URL = required('NEXT_PUBLIC_API_URL', process.env.NEXT_PUBLIC_API_URL);
// Guarded like the two below, not optional: the analytics gate reads this as
// `!== 'production'`, so an unset value silently renders no pixel at all — no
// error, no log, no conversion tracking. It also decides PLATFORM_SCHEME.
export const NODE_ENV = required(
  'NEXT_PUBLIC_NODE_ENV',
  process.env.NEXT_PUBLIC_NODE_ENV
);
// The suffix every platform subdomain hangs off — `timitomi.com` in production,
// `localhost:3001` in development. A host that is not under it carries no slug.
export const PLATFORM_DOMAIN = required(
  'NEXT_PUBLIC_PLATFORM_DOMAIN',
  process.env.NEXT_PUBLIC_PLATFORM_DOMAIN
);
// Mirrors the backend's PLATFORM_SCHEME default. Must be set explicitly on a
// TLS-terminated environment whose NODE_ENV is not literally 'production', or
// the Origin sent from SSR is built on the wrong scheme and is CORS-rejected.
// `||`, not `??`: a key present but blank — the shape an empty Vercel env field
// or a copied `KEY=` line produces — is not nullish, so `??` would keep the
// empty string as the scheme and build `://host`.
export const PLATFORM_SCHEME =
  process.env.NEXT_PUBLIC_PLATFORM_SCHEME ||
  (NODE_ENV === 'production' ? 'https' : 'http');
export const SUPPORT_EMAIL = process.env.NEXT_PUBLIC_SUPPORT_EMAIL;
export const POSTHOG_KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY;
export const POSTHOG_MOKOBOKO_KEY =
  process.env.NEXT_PUBLIC_POSTHOG_MOKOBOKO_KEY;
export const POSTHOG_HOST = process.env.NEXT_PUBLIC_POSTHOG_HOST;
export const APP_NAME = process.env.NEXT_PUBLIC_APP_NAME ?? 'App';
// A bare origin, whatever shape the variable was set in: /preview compares it
// with event.origin by string, and production had it set with a trailing slash,
// which postMessage's own target parsing forgave for the handshake while the
// comparison then dropped every settings message that followed.
const toOrigin = (value: string | undefined): string | undefined => {
  if (!value) return undefined;
  try {
    return new URL(value).origin;
  } catch {
    return undefined;
  }
};

// Origin of the admin app allowed to drive the /preview route by postMessage.
export const ADMIN_ORIGIN = toOrigin(process.env.NEXT_PUBLIC_ADMIN_ORIGIN);

// '1' publishes each studio's web app manifest and the "Add to Home Screen"
// help. Off, pages still name a home-screen icon and title, which change only
// what a phone shows for a page a visitor adds by hand.
export const HOME_SCREEN_APP_ENABLED =
  process.env.NEXT_PUBLIC_HOME_SCREEN_APP === '1';
